import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

import { VersionUpdateBannerComponent } from './version-update-banner.component';

import { BUILD_VERSION } from '../../../../environments/version';

/** What caused a reload check, which decides whether we reload or ask. */
export type ReloadTrigger = 'navigation' | 'detection' | 'reminder';

/**
 * Minimal surface the watcher needs from the banner. Callback-based rather
 * than Observable-based because output() yields an OutputEmitterRef, which
 * exposes subscribe() but is not an rxjs Observable and cannot be piped.
 */
export interface VersionBannerHandle {
  onReload(callback: () => void): void;
  onRemindLater(callback: () => void): void;
  dismiss(): void;
}

/**
 * Detects new deployments and gets stale browser tabs onto the new bundle.
 *
 * Detection is change-based: the first X-App-Version response header seen
 * after page load is the baseline; any different value later means a deploy
 * happened. Before acting we confirm the new frontend bundle is actually
 * live (version.json differs from the embedded BUILD_VERSION) — the backend
 * deploys minutes before the frontend files, and reloading inside that
 * window would re-baseline the tab on a still-stale bundle.
 *
 * How we act depends on the trigger. A router navigation is a safe moment,
 * so we reload silently and lose no in-progress work. A user who never
 * navigates (scan-station terminals sit on one route all shift) is instead
 * offered a banner. Choosing "Remind me later" latches remindLaterActive,
 * which silences both the header trigger and the silent navigation reload;
 * from then on only the five-minute reminder may prompt again.
 */
@Injectable({ providedIn: 'root' })
export class VersionWatcherService implements OnDestroy {
  static readonly VERSION_HEADER = 'X-App-Version';
  static readonly REMIND_LATER_DELAY_MS = 5 * 60_000; // 5 minutes

  private static readonly RELOAD_ATTEMPT_KEY = 'app-version-reload-attempted';

  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);

  private baselineVersion: string | null = null;
  private updateDetected = false;
  private confirmInFlight = false;
  private remindLaterActive = false;
  private banner: VersionBannerHandle | null = null;
  private reminderTimer: ReturnType<typeof setTimeout> | null = null;

  /** Called once from AppComponent. */
  start(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => void this.checkForSafeReload('navigation'));
  }

  /** Fed by VersionHeaderInterceptor with every response's version header. */
  notifyBackendVersion(version: string | null): void {
    if (!version) {
      return;
    }

    if (this.baselineVersion === null) {
      this.baselineVersion = version;
      return;
    }

    if (version !== this.baselineVersion) {
      this.updateDetected = true;
      void this.checkForSafeReload('detection');
    }
  }

  async checkForSafeReload(trigger: ReloadTrigger = 'navigation'): Promise<void> {
    if (!this.updateDetected || this.confirmInFlight) {
      return;
    }

    // Deferring silences the header trigger and the silent navigation
    // reload alike; only the scheduled reminder breaks through.
    if (this.remindLaterActive && trigger !== 'reminder') {
      return;
    }

    this.confirmInFlight = true;
    try {
      const liveVersion = await this.fetchLiveVersion();
      if (!liveVersion || liveVersion === BUILD_VERSION) {
        // New frontend not fetchable yet — keep updateDetected and retry
        // on the next trigger.
        return;
      }

      if (sessionStorage.getItem(VersionWatcherService.RELOAD_ATTEMPT_KEY) === liveVersion) {
        // Already reloaded once for this version and still stale — avoid a loop.
        return;
      }

      if (trigger === 'navigation') {
        this.applyReload(liveVersion);
        return;
      }

      if (this.banner) {
        // A decision is already pending; do not stack banners.
        return;
      }

      this.presentBanner(liveVersion);
    } finally {
      this.confirmInFlight = false;
    }
  }

  ngOnDestroy(): void {
    this.clearReminder();
  }

  private presentBanner(liveVersion: string): void {
    const banner = this.openBanner();
    this.banner = banner;

    banner.onReload(() => {
      this.closeBanner();
      this.applyReload(liveVersion);
    });

    banner.onRemindLater(() => {
      this.closeBanner();
      this.remindLaterActive = true;
      this.scheduleReminder();
    });
  }

  private closeBanner(): void {
    this.banner?.dismiss();
    this.banner = null;
  }

  private scheduleReminder(): void {
    this.clearReminder();
    this.reminderTimer = setTimeout(() => {
      this.reminderTimer = null;
      void this.checkForSafeReload('reminder');
    }, VersionWatcherService.REMIND_LATER_DELAY_MS);
  }

  private clearReminder(): void {
    if (this.reminderTimer !== null) {
      clearTimeout(this.reminderTimer);
      this.reminderTimer = null;
    }
  }

  private applyReload(liveVersion: string): void {
    sessionStorage.setItem(VersionWatcherService.RELOAD_ATTEMPT_KEY, liveVersion);
    this.reloadPage();
  }

  /** Overridden in tests so the decision logic never touches Material. */
  protected openBanner(): VersionBannerHandle {
    // No duration: the prompt must survive on a terminal nobody is watching.
    const ref = this.snackBar.openFromComponent(VersionUpdateBannerComponent, {
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });

    return {
      onReload: callback => {
        ref.instance.reload.subscribe(callback);
      },
      onRemindLater: callback => {
        ref.instance.remindLater.subscribe(callback);
      },
      dismiss: () => ref.dismiss(),
    };
  }

  protected async fetchLiveVersion(): Promise<string | null> {
    try {
      const response = await fetch(`/version.json?cb=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) {
        return null;
      }

      const body: { version?: string } = await response.json();
      return body.version ?? null;
    } catch {
      return null;
    }
  }

  protected reloadPage(): void {
    window.location.reload();
  }
}
